import { Cloneable } from "./cloneable";
import { Serializable } from "./serializable";
import { Lineitem } from './line-item/line-item.model';
import { Client } from './client/client.model';
import { Discount } from './discount/discount.model';
import { Stylist } from './stylist/stylist.model';
import { LineitemJSON } from './line-item/line-item-json.model';
import { List } from 'immutable';
import { Tax } from "./tax/tax.model";

export interface GiftCardJSON extends LineitemJSON {
    id: number,
    cardNumber: string,
    initialValue: number,
    currentValue: number,
    purchaseDate: Date,
    expirationDate: Date,
    status: number, // 0: active, 1: used, 2: expired
    soldBy: number,
    purchaser: number,
    recipient: number,
    cardType: number // 0: eGiftCard, 1: physical
    giftCardImageURL: string
}

export interface GiftCardSerialized {
    id: number,
    cardNumber: string,
    initialValue: number,
    currentValue: number,
    purchaseDate: Date,
    expirationDate: Date,
    status: number,
    soldBy: number,
    purchaser: number,
    recipient: number,
    cardType: number,
    giftCardImageURL: string
}

export class GiftCard extends Lineitem
  implements Cloneable<GiftCard, GiftCardJSON>, Serializable<GiftCardSerialized> {

  constructor(options: GiftCardJSON) {
    super(options);
    this.id = options.id;
    this.cardNumber = options.cardNumber;
    this.initialValue = options.initialValue;
    this.currentValue = options.currentValue;
    this.purchaseDate = options.purchaseDate;
    this.expirationDate = options.expirationDate;
    this.status = options.status;
    this.soldBy = options.soldBy;
    this.purchaser = options.purchaser;
    this.recipient = options.recipient;
    this.cardType = options.cardType;
    this.giftCardImageURL = options.giftCardImageURL;
    //lineitem
    this.lineItemID = options.lineItemID;
    this.clientID = options.clientID;
    this.client = options.client;
    this.type = options.type;
    this.name = options.name;
    this.price = options.price;
    this.quantity = options.quantity;
    this.isRefund = options.isRefund;
    this.stylistID = options.stylistID;
    this.stylist = options.stylist;
    this.discount = options.discount;
    this.taxRateType = options.taxRateType;
    this.customTaxes = options.customTaxes;
    this.isPhantom = options.isPhantom;
    this.preTaxTotals = 0;
  }


  public readonly id: number;
  public override cardNumber: string = '';
  public override initialValue: number = 0;
  public override currentValue: number = 0;
  public override purchaseDate: Date = new Date();
  public override expirationDate: Date = new Date();
  public override status: number = 0;
  public override soldBy: number = 0;
  public override purchaser: number = 0;
  public override recipient: number = 0;
  public override cardType: number = 0;
  public override giftCardImageURL: string = '';
  //lineitem
  public override lineItemID: number = 0;
  public override clientID: number = 0;
  public override client: Client = {} as Client;
  public override type: string = 'giftCard';
  public override name: string = 'Gift card';
  public override price: number = 0;
  public override quantity: number = 1;
  public override isRefund: number = 0;
  public override stylistID: number = 0;
  public override stylist: Stylist = {} as Stylist;
  public override discount: Discount = {} as Discount;
  public override taxRateType: number = 0;
  public override customTaxes: List<any> = List.of<any>();
  public override isPhantom: boolean = false;
  public override preTaxTotals: number = 0;

  public getID(): number {
    return this.id;
  }

  public setCustomTaxes(customTaxes: List<string>): GiftCard {
    const data: GiftCardJSON = this.toJSON();
    data.customTaxes = customTaxes;
    return new GiftCard(data);
  }

  public setPhantom(isPhantom: boolean): GiftCard {
    const data: GiftCardJSON = this.toJSON();
    data.isPhantom = isPhantom;
    return new GiftCard(data);
  }

  public setLineItemID(newID: number): GiftCard {
    const data: GiftCardJSON = this.toJSON();
    data.lineItemID = newID;
    return new GiftCard(data);
  }

  public setClientID(clientID: number): GiftCard {
    const data: GiftCardJSON = this.toJSON();
    data.clientID = clientID;
    return new GiftCard(data);
  }

  public setClient(client: Client): GiftCard {
    const data: GiftCardJSON = this.toJSON();
    data.client = client;
    data.clientID = client.id;
    return new GiftCard(data);
  }

  public setRecipient(recipient: number): GiftCard {
    const data: GiftCardJSON = this.toJSON();
    data.recipient = recipient;
    return new GiftCard(data);
  }

  public setType(type: string): GiftCard {
    const data: GiftCardJSON = this.toJSON();
    data.type = type;
    return new GiftCard(data);
  }

  public setName(name: string): GiftCard {
    const data: GiftCardJSON = this.toJSON();
    data.name = name;
    return new GiftCard(data);
  }

  public setPrice(price: number): GiftCard {
    const data: GiftCardJSON = this.toJSON();
    data.price = price;
    return new GiftCard(data);
  }

  public setQuantity(quantity: number): GiftCard {
    const data: GiftCardJSON = this.toJSON();
    data.quantity = quantity;
    return new GiftCard(data);
  }

  public setRefund(refund: number): GiftCard {
    const data: GiftCardJSON = this.toJSON();
    data.isRefund = refund;
    return new GiftCard(data);
  }

  public setTaxRateType(taxRateType: number): GiftCard {
    const data: GiftCardJSON = this.toJSON();
    data.taxRateType = taxRateType;
    return new GiftCard(data);
  }

  public setStylist(stylist: Stylist): GiftCard {
    const data: GiftCardJSON = this.toJSON();
    data.stylistID = stylist.id;
    data.stylist = stylist;
    return new GiftCard(data);
  }

  public setGiftCardID(id: number): GiftCard {
    const data: GiftCardJSON = this.toJSON();
    data.giftCardID = id;
    return new GiftCard(data);
  }

  public setDiscount(discount: Discount): GiftCard {
    const data: GiftCardJSON = this.toJSON();
    discount = discount.setID(0);
    data.discount = discount;
    return new GiftCard(data);
  }

  public removeDiscount(): GiftCard {
    const data: GiftCardJSON = this.toJSON();
    data.discount = {} as Discount;
    return new GiftCard(data);
  }

  /** Coerce an API money field to a number; returns 0 for absent values. */
  private static toNumber(value: any): number {
    if (value === null || value === undefined || value === '') {
      return 0;
    }
    const parsed = parseFloat(value);
    return isNaN(parsed) ? 0 : parsed;
  }

  public static parse(params: Record<string, any>): GiftCard {
    // `currentValue` is DECIMAL(10,2) and the API's mysql2 pool does not set
    // `decimalNumbers`, so it arrives as a STRING despite being typed
    // `number` here. That made `currentValue += amount` a string
    // concatenation ("100.00" + 50 = "100.0050"), which then rendered a
    // nonsense balance in the edit modal and loosened its max-load check.
    // `initialValue` is a FLOAT column and does arrive as a number, but it is
    // coerced too so the model has one consistent numeric contract.
    return new GiftCard({
      id: params["id"],
      cardNumber: params["cardNumber"],
      initialValue: GiftCard.toNumber(params["initialValue"]),
      currentValue: GiftCard.toNumber(params["currentValue"]),
      purchaseDate: params["purchaseDate"],
      expirationDate: params["expirationDate"],
      status: params["status"],
      soldBy: params["soldBy"],
      purchaser: params["purchaser"],
      recipient: params["recipient"],
      cardType: params["cardType"],
      giftCardImageURL: params["giftCardImageURL"],
      price: GiftCard.toNumber(params["initialValue"]),
      lineItemID: 0,
      clientID: 0,
      client: {} as Client,
      type: 'giftCard',
      name: 'Gift card',
      quantity: 1,
      isRefund: 0,
      taxRateType: 0,
      stylist: {} as Stylist,
      stylistID: 0,
      discount: params["discount"],
      customTaxes: List.of<string>(),
      isPhantom: false,
    });
  }

  public override toJSON(): GiftCardJSON {
    return Object.assign(super.toJSON(), {
      id: this.id,
      cardNumber: this.cardNumber,
      initialValue: this.initialValue,
      currentValue: this.currentValue,
      purchaseDate: this.purchaseDate,
      expirationDate: this.expirationDate,
      status: this.status,
      soldBy: this.soldBy,
      purchaser: this.purchaser,
      recipient: this.recipient,
      cardType: this.cardType,
      giftCardImageURL: this.giftCardImageURL
    });
  }

  public clone(): GiftCard {
    return new GiftCard(this.toJSON());
  }

  public serialize(): GiftCardSerialized {
    return {
        id: this.id,
        cardNumber: this.cardNumber,
        initialValue: this.initialValue,
        currentValue: this.currentValue,
        purchaseDate: this.purchaseDate,
        expirationDate: this.expirationDate,
        status: this.status,
        soldBy: this.soldBy,
        purchaser: this.purchaser,
        recipient: this.recipient,
        cardType: this.cardType,
        giftCardImageURL: this.giftCardImageURL
    };
  }
}
